<!-- src/lib/components/McpRegisterPanel.svelte -->
<!--
  Claude Code MCP registration panel. Lives under Settings → Integrations.

  One-click register the agentboard MCP server in either:
    - Global (~/.claude.json)           → recommended default
    - Project (<repo>/.mcp.json)        → advanced, rarely needed

  The backend (routes/mcp_register.rs) round-trips the JSON through
  serde_json::Value so we never clobber unrelated config keys.
-->
<script lang="ts">
  import { api } from '../api.ts';
  import { platform } from '../platform.ts';

  interface RegisterStatus {
    global_registered:  boolean;
    project_registered: boolean;
    global_path:        string | null;
    project_path:       string | null;
    mcp_url:            string;
  }

  let status     = $state<RegisterStatus | null>(null);
  let loading    = $state(true);
  let errorMsg   = $state<string | null>(null);
  let actionMsg  = $state<string | null>(null);
  let pickedRepo = $state<string | null>(null);
  let busy       = $state(false);

  async function loadStatus() {
    loading = true;
    errorMsg = null;
    try {
      const qs = pickedRepo ? `?project_path=${encodeURIComponent(pickedRepo)}` : '';
      status = await api<RegisterStatus>(`/api/v1/mcp/register/status${qs}`);
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to load status';
      status = null;
    } finally {
      loading = false;
    }
  }

  $effect(() => { void loadStatus(); });

  async function registerGlobal() {
    if (busy) return;
    busy = true;
    errorMsg = null;
    actionMsg = null;
    try {
      await postRegister({ scope: 'user' });
      actionMsg = 'Registered globally. Restart Claude Code for it to pick up the new server.';
      await loadStatus();
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to register';
    } finally {
      busy = false;
    }
  }

  async function unregisterGlobal() {
    if (busy) return;
    if (!window.confirm('Remove Agentboard from your global ~/.claude.json?')) return;
    busy = true;
    errorMsg = null;
    actionMsg = null;
    try {
      await deleteRegister({ scope: 'user' });
      actionMsg = 'Removed from ~/.claude.json.';
      await loadStatus();
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to unregister';
    } finally {
      busy = false;
    }
  }

  async function pickRepo() {
    // Dynamic import pattern already used in FileExplorer — returns null
    // outside Tauri and we fall back to a prompt.
    let path: string | null = await platform.selectDirectory();
    if (path == null) {
      const entered = window.prompt(
        'Enter the absolute path to the repo where you want .mcp.json written:',
        pickedRepo ?? '',
      );
      path = entered && entered.trim() ? entered.trim() : null;
    }
    if (path) {
      pickedRepo = path;
      await loadStatus();
    }
  }

  async function registerProject() {
    if (busy || !pickedRepo) return;
    // Extra confirmation — this file gets committed to git and leaks
    // your localhost URL to anyone who pulls the repo.
    const confirmMsg =
      `Write .mcp.json to:\n\n${pickedRepo}\n\n` +
      `Note: this file is usually committed to git, so anyone who ` +
      `clones the repo will see this localhost URL. Continue?`;
    if (!window.confirm(confirmMsg)) return;

    busy = true;
    errorMsg = null;
    actionMsg = null;
    try {
      await postRegister({ scope: 'project', project_path: pickedRepo });
      actionMsg = `Wrote ${pickedRepo}/.mcp.json. Restart Claude Code inside that repo.`;
      await loadStatus();
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to register project';
    } finally {
      busy = false;
    }
  }

  async function unregisterProject() {
    if (busy || !pickedRepo) return;
    if (!window.confirm(`Remove Agentboard from ${pickedRepo}/.mcp.json?`)) return;
    busy = true;
    errorMsg = null;
    actionMsg = null;
    try {
      await deleteRegister({ scope: 'project', project_path: pickedRepo });
      actionMsg = `Removed from ${pickedRepo}/.mcp.json.`;
      await loadStatus();
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to unregister project';
    } finally {
      busy = false;
    }
  }

  async function postRegister(body: { scope: string; project_path?: string }) {
    await api('/api/v1/mcp/register', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });
  }
  async function deleteRegister(body: { scope: string; project_path?: string }) {
    await api('/api/v1/mcp/register', {
      method:  'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });
  }
</script>

<div class="mcp-panel">
  <p class="intro">
    Hook Claude Code up to Agentboard so it can read your boards, spawn agents,
    and update cards via MCP tools.
  </p>

  {#if loading}
    <p class="muted">Checking registration…</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if status}
    <div class="scope-row">
      <div class="scope-info">
        <div class="scope-title">
          <span class="label">Global</span>
          <span class="badge" class:on={status.global_registered}>
            {status.global_registered ? 'Installed' : 'Not installed'}
          </span>
          <span class="pill recommended">Recommended</span>
        </div>
        <p class="scope-hint">
          Writes to <code>~/.claude.json</code>. Available in every project,
          every Claude Code session on this Mac.
        </p>
      </div>
      <div class="scope-actions">
        {#if status.global_registered}
          <button class="btn secondary" onclick={unregisterGlobal} disabled={busy}>
            Remove
          </button>
        {:else}
          <button class="btn primary" onclick={registerGlobal} disabled={busy}>
            {busy ? 'Installing…' : 'Install globally'}
          </button>
        {/if}
      </div>
    </div>

    <div class="divider"></div>

    <div class="scope-row">
      <div class="scope-info">
        <div class="scope-title">
          <span class="label">Per-repo</span>
          {#if status.project_registered}
            <span class="badge on">Installed</span>
          {/if}
        </div>
        <p class="scope-hint">
          Writes to <code>&lt;repo&gt;/.mcp.json</code>. Usually committed to
          git — <strong>your localhost URL will be visible to anyone who clones</strong>.
          Only useful if you run multiple Agentboard instances on different ports.
        </p>
        {#if pickedRepo}
          <p class="picked" title={pickedRepo}>Target: <code>{pickedRepo}</code></p>
        {/if}
      </div>
      <div class="scope-actions">
        <button class="btn secondary" onclick={pickRepo} disabled={busy}>
          {pickedRepo ? 'Change…' : 'Pick repo…'}
        </button>
        {#if pickedRepo && status.project_registered}
          <button class="btn secondary" onclick={unregisterProject} disabled={busy}>
            Remove
          </button>
        {:else if pickedRepo}
          <button class="btn primary" onclick={registerProject} disabled={busy}>
            {busy ? 'Installing…' : 'Install here'}
          </button>
        {/if}
      </div>
    </div>

    <div class="divider"></div>

    <p class="url-line">
      MCP endpoint: <code>{status.mcp_url}</code>
    </p>
  {/if}

  {#if actionMsg}
    <p class="action-msg">{actionMsg}</p>
  {/if}
</div>

<style>
  .mcp-panel {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    padding: 0.25rem 0;
    font-size: 0.78rem;
    color: oklch(0.80 0.05 310 / 0.85);
  }

  .intro {
    margin: 0;
    color: oklch(0.72 0.05 310 / 0.75);
    line-height: 1.5;
  }

  .scope-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .scope-info { flex: 1; min-width: 0; }
  .scope-actions {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    flex-shrink: 0;
  }

  .scope-title {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.25rem;
    flex-wrap: wrap;
  }
  .label {
    font-size: 0.82rem;
    font-weight: 600;
    color: oklch(0.92 0.06 310);
  }

  .badge {
    font-size: 0.6rem;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    background: oklch(0.22 0.03 275 / 0.6);
    color: oklch(0.60 0.05 310 / 0.7);
    border: 1px solid oklch(0.35 0.05 275 / 0.4);
  }
  .badge.on {
    background: oklch(0.78 0.16 150 / 0.18);
    color: oklch(0.86 0.20 150);
    border-color: oklch(0.78 0.16 150 / 0.5);
  }

  .pill.recommended {
    font-size: 0.6rem;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: oklch(0.30 0.18 310 / 0.40);
    color: oklch(0.92 0.12 310);
    border: 1px solid oklch(0.72 0.26 310 / 0.45);
    font-weight: 600;
  }

  .scope-hint {
    margin: 0.15rem 0 0 0;
    font-size: 0.72rem;
    color: oklch(0.60 0.05 310 / 0.70);
    line-height: 1.5;
  }
  .scope-hint strong {
    color: oklch(0.78 0.16 85 / 0.9);
  }

  .picked {
    margin: 0.35rem 0 0 0;
    font-size: 0.68rem;
    color: oklch(0.72 0.05 310 / 0.75);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .divider {
    height: 1px;
    background: oklch(0.30 0.05 275 / 0.15);
    margin: 0.15rem 0;
  }

  .btn {
    font-size: 0.7rem;
    font-weight: 500;
    padding: 0.4rem 0.7rem;
    border-radius: 3px;
    cursor: pointer;
    transition: background 100ms ease, border-color 100ms ease;
    white-space: nowrap;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn.primary {
    background: oklch(0.30 0.18 310 / 0.45);
    color: oklch(0.96 0.06 310);
    border: 1px solid oklch(0.72 0.26 310 / 0.45);
  }
  .btn.primary:hover:not(:disabled) {
    background: oklch(0.38 0.22 310 / 0.58);
  }
  .btn.secondary {
    background: oklch(0.17 0.022 275);
    color: oklch(0.80 0.05 310 / 0.85);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
  }
  .btn.secondary:hover:not(:disabled) {
    background: oklch(0.22 0.03 275);
    border-color: oklch(0.40 0.08 310 / 0.5);
  }

  .url-line {
    margin: 0;
    font-size: 0.7rem;
    color: oklch(0.55 0.05 310 / 0.6);
  }

  .action-msg {
    margin: 0.25rem 0 0 0;
    padding: 0.5rem 0.65rem;
    font-size: 0.72rem;
    border-radius: 3px;
    background: oklch(0.78 0.16 150 / 0.08);
    color: oklch(0.86 0.18 150 / 0.95);
    border: 1px solid oklch(0.78 0.16 150 / 0.35);
    line-height: 1.5;
  }

  .error {
    margin: 0;
    padding: 0.5rem 0.65rem;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    color: oklch(0.72 0.22 25 / 0.9);
    background: oklch(0.68 0.24 25 / 0.08);
    border: 1px solid oklch(0.68 0.24 25 / 0.3);
    border-radius: 3px;
  }
  .muted {
    margin: 0;
    color: oklch(0.55 0.05 310 / 0.5);
    font-size: 0.72rem;
  }

  code {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.92em;
    padding: 0.05rem 0.3rem;
    background: oklch(0.22 0.03 275 / 0.5);
    border-radius: 2px;
  }
</style>
